import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, TrendingUp, ArrowRight, ChevronRight, Wallet, Star } from 'lucide-react';
import { benefitEstimate } from '../data/kmsServices';

const ACCENT_BG = {
  emerald: 'from-emerald-50 to-emerald-100/40 ring-emerald-200/60',
  sky:     'from-sky-50 to-sky-100/40 ring-sky-200/60',
  amber:   'from-amber-50 to-amber-100/40 ring-amber-200/60',
  rose:    'from-rose-50 to-rose-100/40 ring-rose-200/60',
  green:   'from-green-50 to-green-100/40 ring-green-200/60',
  indigo:  'from-indigo-50 to-indigo-100/40 ring-indigo-200/60',
  violet:  'from-violet-50 to-violet-100/40 ring-violet-200/60',
};
const ACCENT_TEXT = {
  emerald: 'text-emerald-700', sky: 'text-sky-700', amber: 'text-amber-700',
  rose: 'text-rose-700', green: 'text-green-700', indigo: 'text-indigo-700', violet: 'text-violet-700',
};

const inr = (n) => '₹' + Number(n).toLocaleString('en-IN');

/**
 * "You may be leaving money on the table" banner (Feature b).
 * Logged-in: shows real eligible-scheme count + estimated yearly benefit.
 * Guest: prompts sign-in / KMS Smart run.
 */
export const BenefitBanner = ({ user }) => {
  const navigate = useNavigate();
  const [count, setCount] = useState(null);
  const est = benefitEstimate(user);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/subscription/eligibility-count?user_id=${user.id}`
        );
        if (res.ok) {
          const d = await res.json();
          if (alive && typeof d.total_eligible === 'number') setCount(d.total_eligible);
        }
      } catch { /* silent — banner still shows the estimate */ }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  const isGuest = !user?.id;

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-[#052E1F] text-white p-6 sm:p-8 mb-8 shadow-[0_20px_60px_-20px_rgba(5,46,31,0.5)]"
      data-testid="foryou-benefit-banner"
    >
      <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-emerald-400/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-12 h-48 w-48 rounded-full bg-green-400/15 blur-3xl" />

      <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 lg:justify-between">
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-300 ring-1 ring-amber-400/25 mb-3">
            <Wallet className="h-3.5 w-3.5" /> Money on the table
          </div>

          {isGuest ? (
            <>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight">
                You could be missing out on{' '}
                <span className="text-emerald-300">{inr(est.minYear)}+/year</span> in benefits.
              </h2>
              <p className="mt-2 text-sm sm:text-base text-emerald-100/80 max-w-2xl">
                Tell KMS Smart who you are and we'll match you to every government scheme, subsidy and job you qualify for — free, in under a minute.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight">
                {count !== null ? (
                  <>You likely qualify for <span className="text-emerald-300" data-testid="foryou-eligible-count">{count}</span> government benefits.</>
                ) : (
                  <>You likely qualify for dozens of government benefits.</>
                )}
              </h2>
              <p className="mt-2 text-sm sm:text-base text-emerald-100/80 max-w-2xl">
                Citizens like you claim an estimated{' '}
                <span className="font-semibold text-white">{inr(est.minYear)}+/year</span> through {est.label}. See exactly what's yours — and how to claim it.
              </p>
            </>
          )}
        </div>

        <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0">
          <button
            onClick={() => navigate('/impact-analyser')}
            className="group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-white text-slate-900 font-semibold text-sm hover:bg-slate-100 active:scale-[0.98] transition"
            data-testid="foryou-banner-cta"
          >
            <Sparkles className="h-4 w-4 text-amber-500" />
            {isGuest ? 'Check what I qualify for' : 'See my benefits'}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          {isGuest && (
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center justify-center h-12 px-6 rounded-full border border-white/25 text-white font-medium text-sm hover:bg-white/10 active:scale-[0.98] transition"
              data-testid="foryou-banner-login"
            >
              Sign in
            </button>
          )}
        </div>
      </div>

      <div className="relative mt-4 flex items-center gap-1.5 text-[11px] text-emerald-200/60">
        <TrendingUp className="h-3.5 w-3.5" /> Estimates based on real central & state schemes · Actual benefits depend on your profile
      </div>
    </div>
  );
};

/**
 * "Recommended for you" strip (Feature a).
 * Only renders when there are occupation-based recommendations.
 */
export const RecommendedStrip = ({ recommended }) => {
  const navigate = useNavigate();
  if (!recommended || recommended.length === 0) return null;

  return (
    <section className="mb-8" data-testid="foryou-recommended-strip">
      <div className="flex items-center gap-2 mb-3">
        <Star className="h-4 w-4 text-amber-500 fill-amber-400" />
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">Recommended for you</h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {recommended.map((s) => (
          <button
            key={s.slug}
            onClick={() => navigate(`/money-hub/${s.slug}`)}
            data-testid={`foryou-rec-${s.slug}`}
            className={`group relative text-left rounded-xl bg-gradient-to-br ${ACCENT_BG[s.accent]} ring-1 p-4 transition hover:shadow-lg hover:-translate-y-0.5`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-2xl leading-none">{s.icon}</div>
              <span className={`text-[9px] font-bold uppercase tracking-wide ${ACCENT_TEXT[s.accent]} bg-white/70 rounded-full px-2 py-0.5`}>
                For you
              </span>
            </div>
            <h3 className="mt-3 text-sm font-semibold text-gray-900 leading-tight">{s.title}</h3>
            <p className="mt-1 text-[11px] text-gray-600 leading-relaxed line-clamp-2">{s.subtitle}</p>
            <p className={`mt-2.5 text-[10px] font-semibold uppercase tracking-wide ${ACCENT_TEXT[s.accent]} inline-flex items-center gap-0.5`}>
              Explore <ChevronRight className="w-3 h-3" />
            </p>
          </button>
        ))}
      </div>
    </section>
  );
};
